import { FILE_TYPE_GROUPS } from "../config/fileTypeConfig";

// Resolves the first valid value (non-null, non-undefined)
export const resolveFirst = (...sources) =>
  sources.find((v) => v !== undefined && v !== null) ?? null;

export const getRemarksModalTitle = (remarksModal) => {
  const { entityType, action } = remarksModal;

  if (!entityType || !action) return "Update Status";

  /* ========================= */
  /* 🟦 Selection (Creator) */
  /* ========================= */
  if (entityType === "influencer") {
    if (action === "approve") return "Select Creator";
    if (action === "reject") return "Mark Not Interested";
    if (action === "hold") return "Put Creator On Hold";
  }

  /* ========================= */
  /* 🟨 Confirmation Section   */
  /* ========================= */
  if (entityType === "confirmation") {
    if (action === "shipped") return "Mark as Shipped";
    if (action === "not shipped") return "Mark as Not Shipped";
    if (action === "on hold") return "Put Confirmation On Hold";
    if (action === "delivered") return "Mark as Delivered";
    if (action === "returned") return "Mark as Returned";
    if (action === "rto") return "Mark as RTO";
  }

  /* ========================= */
  /* 🟩 Deliverable Section    */
  /* ========================= */
  if (entityType === "deliverable") {
    if (action === "approve") return "Approve Deliverable";
    if (action === "reject") return "Mark Don't Like";
    if (action === "hold") return "Put Deliverable On Hold";
  }

  return "Update Status";
};

export const getPlatformLink = (platforms = [], name = "") =>
  platforms.find((p) => p.platformName?.toLowerCase() === name.toLowerCase())
    ?.profileURL || "";

export const getPlatformValues = (platforms = [], field) =>
  platforms
    .map((p) => p?.[field])
    .filter(Boolean)
    .join(", ");

export const getPrimaryPlatform = (platforms = []) => {
  if (!platforms.length) return null;

  const instagram = platforms.find(
    (p) => p.platformName?.toLowerCase() === "instagram"
  );

  return instagram || platforms[0];
};

export const getRemainingPlatforms = (platforms = [], primary) =>
  platforms.filter(
    (p) => p.platformName && p.platformName !== primary?.platformName
  );

export const joinPlatformField = (platforms = [], field) =>
  platforms
    .map((p) => p?.[field])
    .filter(Boolean)
    .join(", ");

export const getCampaignPurpose = (campaign) => {
  if (!campaign?.confirmation && !campaign?.deliverable) return "selection";
  if (campaign?.confirmation && !campaign?.deliverable) return "confirmation";
  if (campaign?.confirmation && campaign?.deliverable) return "deliverable";
};

// export const getProductWorth = (c) =>
//   Number(c.cashValue) || Number(c.perInfluencerBudget) || 0;

export const getProductWorth = (c) => {
  const raw = c.cashValue ?? c.perInfluencerBudget ?? null;

  if (!raw) return { min: 0, max: 0 };

  // 🔹 Case 1: already number
  if (typeof raw === "number") {
    return { min: raw, max: raw };
  }

  // 🔹 Case 2: string range "2000-5000"
  if (typeof raw === "string" && raw.includes("-")) {
    const [min, max] = raw.split("-").map((v) => Number(v.trim()));
    return {
      min: isNaN(min) ? 0 : min,
      max: isNaN(max) ? 0 : max,
    };
  }

  // 🔹 Case 3: string number "2000"
  const num = Number(raw);
  return {
    min: isNaN(num) ? 0 : num,
    max: isNaN(num) ? 0 : num,
  };
};

// export const parseRangeToNumbers = (rangeStr) => {
//   if (!rangeStr) return [0, 1000000];

//   const parse = (val) => {
//     if (val.includes("Cr")) return parseFloat(val) * 10000000;
//     if (val.includes("L")) return parseFloat(val) * 100000;
//     if (val.includes("K")) return parseFloat(val) * 1000;
//     return Number(val);
//   };

//   const [min, max] = rangeStr.split("-").map((s) => parse(s.trim()));

//   return [min || 0, max || 1000000];
// };

export const parseRangeToNumbers = (rangeStr) => {
  if (!rangeStr) return [0, Infinity];

  const parse = (val) => {
    if (!val) return 0;

    const v = val.trim();

    if (v === "∞") return Infinity;

    if (v.includes("Cr")) return parseFloat(v) * 10000000;
    if (v.includes("L")) return parseFloat(v) * 100000;
    if (v.includes("K")) return parseFloat(v) * 1000;

    return Number(v);
  };

  // ✅ handle "10L+" format
  if (rangeStr.includes("+")) {
    const min = parse(rangeStr.replace("+", ""));
    return [min || 0, Infinity];
  }

  // ✅ handle normal "min-max"
  const [minStr, maxStr] = rangeStr.split("-");

  const min = parse(minStr);
  const max = parse(maxStr);

  return [
    min || 0,
    max === Infinity ? Infinity : max || Infinity, // ✅ no fake cap
  ];
};

export const COPYRIGHT_TEXT = `© ${new Date().getFullYear()}, Hobo.Video. All Rights Reserved.`;

export function formatDateTime(date) {
  if (!date) return "-";

  return new Date(date).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

// export function formatVisitDateTimeRange(
//   startDate,
//   endDate,
//   startTime,
//   endTime
// ) {
//   if (!startDate || !endDate) return "Not specified";

//   const formatDate = (date) =>
//     new Date(date).toLocaleDateString("en-IN", {
//       day: "2-digit",
//       month: "short",
//       year: "numeric",
//     });

//   const formatTime = (time) => {
//     if (!time) return "--";

//     // already AM/PM
//     if (
//       time.toLowerCase().includes("am") ||
//       time.toLowerCase().includes("pm")
//     ) {
//       return time;
//     }

//     const [hour, minute] = time.split(":");
//     let h = parseInt(hour, 10);
//     const ampm = h >= 12 ? "PM" : "AM";

//     h = h % 12;
//     if (h === 0) h = 12;

//     return `${h}:${minute} ${ampm}`;
//   };

//   return `${formatDate(startDate)} - ${formatDate(endDate)} | ${formatTime(
//     startTime
//   )} - ${formatTime(endTime)}`;
// }

export const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

export function formatVisitDateTimeRange(
  startDate,
  endDate,
  startTime,
  endTime,
  exactDate,
  exactTime
) {
  const formatTime = (time) => {
    if (!time) return "--";

    const lower = String(time).toLowerCase();

    // already formatted
    if (lower.includes("am") || lower.includes("pm")) {
      return time.toUpperCase();
    }

    const [hour = "0", minute = "00"] = time.split(":");

    let h = parseInt(hour, 10);
    const ampm = h >= 12 ? "PM" : "AM";

    h = h % 12;
    if (h === 0) h = 12;

    return `${h}:${minute} ${ampm}`;
  };

  // ✅ Exact date/time support
  if (exactDate || exactTime) {
    const datePart = exactDate ? formatDate(exactDate) : "Not specified";
    const timePart = exactTime ? formatTime(exactTime) : "--";

    return `${datePart} | ${timePart}`;
  }

  // ✅ Existing range support
  if (!startDate || !endDate) return "Not specified";

  return `${formatDate(startDate)} - ${formatDate(endDate)} | ${formatTime(
    startTime
  )} - ${formatTime(endTime)}`;
}

export function getRandomColor(category) {
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;

  return `hsl(${hue}, 70%, 85%)`;
}

export function getRandomTextColor(category) {
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = hash % 360;
  return `hsl(${hue}, 80%, 30%)`;
}

// Custom Question's Answers
export const getCustomAnswer = (influencer, questionText) => {
  if (
    !influencer ||
    !influencer.creatorAnswers ||
    !Array.isArray(influencer.creatorAnswers)
  ) {
    return null;
  }

  const answerObj = influencer.creatorAnswers.find(
    (item) => item.question === questionText
  );

  return answerObj ? answerObj.answer : null;
};

export const resolveAllowedTypes = (allowedTypes = []) => {
  return Array.from(
    new Set(
      allowedTypes.flatMap((key) => {
        const group = FILE_TYPE_GROUPS[key];
        return group ? group.patterns : [];
      })
    )
  );
};
